import { useRef, useEffect, ReactNode } from 'react';
import { motion, useAnimation, PanInfo } from 'framer-motion';
import { useMapStore } from '../../stores/mapStore';
import type { SheetState } from '../../types';

interface BottomSheetProps {
  children: ReactNode;
}

// Sheet heights as percentage of viewport
const SHEET_HEIGHTS: Record<SheetState, string> = {
  minimized: '2vh',
  collapsed: '8vh',
  half: '40vh',
  expanded: '85vh',
};

const SHEET_SNAP_POINTS = {
  minimized: 0.02,
  collapsed: 0.08,
  half: 0.40,
  expanded: 0.85,
};

export function BottomSheet({ children }: BottomSheetProps) {
  const { sheetState, setSheetState } = useMapStore();
  const controls = useAnimation();
  const sheetRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);

  useEffect(() => {
    const isMinimizing = sheetState === 'minimized';
    controls.start({
      height: SHEET_HEIGHTS[sheetState],
      transition: isMinimizing
        ? { type: 'spring', damping: 35, stiffness: 150 }
        : { type: 'spring', damping: 30, stiffness: 300 },
    });
  }, [sheetState, controls]);

  const handleDragStart = () => {
    isDragging.current = true;
  };

  const handleDragEnd = (_: any, info: PanInfo) => {
    isDragging.current = false;
    const velocity = info.velocity.y;
    const offset = info.offset.y;
    const currentHeight = sheetRef.current?.offsetHeight || 0;
    const viewportHeight = window.innerHeight;

    // Calculate the actual height after drag
    const draggedHeight = currentHeight - offset;
    const currentRatio = draggedHeight / viewportHeight;

    // Determine target state based on velocity and position
    let targetState: SheetState;

    if (velocity < -500) {
      // Fast upward swipe - expand one level
      if (sheetState === 'minimized') targetState = 'collapsed';
      else if (sheetState === 'collapsed') targetState = 'half';
      else targetState = 'expanded';
    } else if (velocity > 500) {
      // Fast downward swipe - collapse one level
      if (sheetState === 'expanded') targetState = 'half';
      else if (sheetState === 'half') targetState = 'collapsed';
      else targetState = 'minimized';
    } else {
      // Snap to nearest point based on current position
      const distances = {
        minimized: Math.abs(currentRatio - SHEET_SNAP_POINTS.minimized),
        collapsed: Math.abs(currentRatio - SHEET_SNAP_POINTS.collapsed),
        half: Math.abs(currentRatio - SHEET_SNAP_POINTS.half),
        expanded: Math.abs(currentRatio - SHEET_SNAP_POINTS.expanded),
      };

      targetState = (Object.entries(distances).reduce((a, b) =>
        a[1] < b[1] ? a : b
      )[0] as SheetState);
    }

    setSheetState(targetState);
  };

  const handleHandleClick = () => {
    // Only toggle if not dragging
    if (isDragging.current) return;

    // Cycle through states on click
    if (sheetState === 'minimized') {
      setSheetState('collapsed');
    } else if (sheetState === 'collapsed') {
      setSheetState('half');
    } else if (sheetState === 'half') {
      setSheetState('expanded');
    } else {
      setSheetState('minimized');
    }
  };

  const handleHandleDoubleClick = () => {
    // Double click toggles between minimized and expanded
    if (sheetState === 'expanded') {
      setSheetState('minimized');
    } else {
      setSheetState('expanded');
    }
  };

  return (
    <motion.div
      ref={sheetRef}
      data-tour-target="bottom-sheet"
      className="fixed bottom-0 left-0 right-0 bg-white/95 dark:bg-gray-900/95 backdrop-blur-xl rounded-t-3xl shadow-sheet z-50 safe-area-bottom"
      initial={{ height: SHEET_HEIGHTS.half }}
      animate={controls}
      drag="y"
      dragConstraints={{ top: 0, bottom: 0 }}
      dragElastic={0.2}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      style={{ touchAction: 'none' }}
    >
      {/* Below-viewport fill — travels with the sheet so no gap shows when dragged up */}
      <div
        className="absolute left-0 right-0 bg-white/95 dark:bg-gray-900/95 pointer-events-none"
        style={{ top: '100%', height: '300px' }}
      />
      {/* Drag Handle - clickable area */}
      <div
        className="flex justify-center pt-3 pb-2 cursor-grab active:cursor-grabbing"
        onClick={handleHandleClick}
        onDoubleClick={handleHandleDoubleClick}
      >
        <div className="w-10 h-1 bg-gray-300 dark:bg-gray-600 rounded-full hover:bg-gray-400 dark:hover:bg-gray-500 transition-colors" />
      </div>

      {/* Content */}
      <div className="overflow-y-auto h-[calc(100%-24px)] px-4 pb-4 overscroll-contain">
        {children}
      </div>
    </motion.div>
  );
}
